import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  TCreateHealthSubscriptionInput,
  TCreateHealthSubscriptionOutput,
  THealthFormContent,
  TSimulateHealthSubscriptionInput,
  TSimulateHealthSubscriptionOutput,
} from "./types";
import { fetchJson } from "../../../utils/authenticatedFetch";
import { mockHealthResponse } from "./data";
import { isDev } from "../../../env";

export const useGetHealthFormQuery = (
  options?: Omit<UseQueryOptions<THealthFormContent, Error>, "queryFn" | "queryKey">,
) => {
  return useQuery<THealthFormContent, Error>({
    ...options,
    queryKey: ["getSubscriptionPage"],
    queryFn: async () => {
      if (isDev) await new Promise((resolve) => setTimeout(resolve, 500));

      const response = await fetchJson<any>("/Health/GetSubscriptionPage");

      return {
        maxAgeSubscription: response.maxAgeSubscription,
        documentos: {
          id: "documentos",
          type: "documentList",
          documents: response.documentos,
        },
        documentosPlano: {
          id: "documentosPlano",
          type: "documentList",
          documents: response.documentosPlano,
        },
        termosECondicoes: {
          id: "termosECondicoes",
          type: "checkboxList",
          checkboxes: response.termosECondicoes,
        },
        termosECondicoesPlano: {
          id: "termosECondicoesPlano",
          type: "checkboxList",
          checkboxes: response.termosECondicoesPlano,
        },
      } as THealthFormContent;
    },
  });
};

export const useSimulateMutation = (
  paymentFrequency: string,
  options?: Omit<
    UseMutationOptions<TSimulateHealthSubscriptionOutput, Error, TSimulateHealthSubscriptionInput>,
    "mutationFn" | "mutationKey"
  >,
) => {
  return useMutation<TSimulateHealthSubscriptionOutput, Error, TSimulateHealthSubscriptionInput>({
    ...options,
    mutationKey: ["useSimulateMutation", paymentFrequency],
    mutationFn: async (input: TSimulateHealthSubscriptionInput) => {
      const response = await fetchJson<TSimulateHealthSubscriptionOutput>("/Health/Simulate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });

      if (isDev) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return mockHealthResponse(
          input.healthInsurancePlan.moduleType,
          input.healthInsurancePlan.paymentFrequency,
          input.healthInsurancePlan.supplementaryHealthPlan,
          input.healthInsurancePlan.supplementaryHealthPaymentFrequency,
        );
      }

      return response;
    },
  });
};

export const useCreateMutation = (
  moduleType: string,
  options?: Omit<
    UseMutationOptions<TCreateHealthSubscriptionOutput, Error, TCreateHealthSubscriptionInput>,
    "mutationFn" | "mutationKey"
  >,
) => {
  return useMutation<TCreateHealthSubscriptionOutput, Error, TCreateHealthSubscriptionInput>({
    ...options,
    mutationKey: ["useCreateMutation", moduleType],
    mutationFn: async (input: TCreateHealthSubscriptionInput) => {
      const response = await fetchJson<TCreateHealthSubscriptionOutput>("/Health/Create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });

      if (isDev) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        return mockHealthResponse(
          input.healthInsurancePlan.moduleType,
          input.healthInsurancePlan.paymentFrequency,
          input.healthInsurancePlan.supplementaryHealthPlan,
          input.healthInsurancePlan.supplementaryHealthPaymentFrequency,
        );
      }

      return response;
    },
  });
};
